// frontend/src/pages/RelatoriosPage/RelatoriosPortadorTab.jsx
//
// Aba "Por portador" dos Relatórios: consolida gastos de cartão de crédito
// por portador (cartão adicional) em um período livre — ranking, composição
// por categoria, participação e detalhamento.

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
    Card, Form, Row, Col, DatePicker, Select, Segmented, Button, Statistic,
    Table, Typography, message, Space, Tag, Tooltip,
} from 'antd';
import { FilterOutlined, FileExcelOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, Legend,
} from 'recharts';
import * as XLSX from 'xlsx';
import { getRelatorioPorPortador } from '../../services/financeService';
import { chartPalette } from '../../styles/brandColors';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const fmtBRL = (v) =>
    parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ComposicaoTooltip = ({ active, payload, label, modo }) => {
    if (!active || !payload?.length) return null;
    const itens = payload.filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
    if (itens.length === 0) return null;
    return (
        <div
            style={{
                background: 'rgba(31, 79, 79, 0.95)',
                color: '#fff',
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 12,
                minWidth: 180,
            }}
        >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
            {itens.map((p, i) => (
                <div
                    key={p.dataKey}
                    style={{ display: 'flex', gap: 8, fontWeight: i === 0 ? 700 : 400 }}
                >
                    <span
                        style={{
                            width: 8,
                            height: 8,
                            background: p.color,
                            borderRadius: 2,
                            alignSelf: 'center',
                        }}
                    />
                    <span style={{ flex: 1 }}>
                        {p.dataKey}
                        {i === 0 ? ' — maior gasto' : ''}
                    </span>
                    <span>{modo === 'pct' ? `${p.value.toFixed(0)}%` : fmtBRL(p.value)}</span>
                </div>
            ))}
        </div>
    );
};

const catColor = (key, idx) => (key === 'Outras' ? '#B7C4C4' : chartPalette[idx % chartPalette.length]);

const DEFAULT_RANGE = [dayjs().subtract(2, 'month').startOf('month'), dayjs().endOf('month')];

const PRESETS = [
    { label: 'Este mês', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
    {
        label: 'Mês passado',
        value: [
            dayjs().subtract(1, 'month').startOf('month'),
            dayjs().subtract(1, 'month').endOf('month'),
        ],
    },
    { label: 'Últimos 3 meses', value: DEFAULT_RANGE },
    {
        label: 'Últimos 6 meses',
        value: [dayjs().subtract(5, 'month').startOf('month'), dayjs().endOf('month')],
    },
    { label: 'Este ano', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
];

const MAX_CATEGORIAS = 8;

const RelatoriosPortadorTab = ({ cartoes = [], isMobile = false }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [regime, setRegime] = useState('fatura');
    const [groupBy, setGroupBy] = useState('adicional');

    const fetchDados = useCallback(async () => {
        setLoading(true);
        try {
            const values = form.getFieldsValue();
            const range = values.periodo || DEFAULT_RANGE;
            const params = {
                start_date: range[0].format('YYYY-MM-DD'),
                end_date: range[1].format('YYYY-MM-DD'),
                regime,
                group_by: groupBy,
            };
            if (values.cartoes?.length) params.cartoes = values.cartoes.join(',');
            const res = await getRelatorioPorPortador(params);
            setData(res);
        } catch (error) {
            console.error('Erro no relatório por portador', error);
            message.error('Falha ao carregar o relatório por portador.');
        } finally {
            setLoading(false);
        }
    }, [form, regime, groupBy]);

    useEffect(() => {
        fetchDados();
    }, [fetchDados]);

    const portadores = useMemo(() => data?.portadores || [], [data]);
    const titular = data?.titular || null;

    // Rótulo de exibição: desambigua portadores homônimos com o nome do cartão
    // (e, se ainda colidir, com um contador). Nomes únicos ficam como estão.
    const rotuloPorChave = useMemo(() => {
        const contagem = {};
        portadores.forEach((p) => {
            contagem[p.portador] = (contagem[p.portador] || 0) + 1;
        });
        const usados = {};
        const map = {};
        portadores.forEach((p) => {
            let rotulo = p.portador;
            if (contagem[p.portador] > 1) {
                rotulo = p.cartao_nome ? `${p.portador} · ${p.cartao_nome}` : p.portador;
                usados[rotulo] = (usados[rotulo] || 0) + 1;
                if (usados[rotulo] > 1) rotulo = `${rotulo} (${usados[rotulo]})`;
            }
            map[p.chave] = rotulo;
        });
        return map;
    }, [portadores]);

    const rankingData = useMemo(() => {
        const linhas = portadores.map((p) => ({
            chave: p.chave,
            nome: rotuloPorChave[p.chave] || p.portador,
            total: p.total,
        }));
        if (titular && titular.total > 0) {
            linhas.push({ chave: 'titular', nome: 'Titular', total: titular.total, isTitular: true });
        }
        return linhas.sort((a, b) => b.total - a.total);
    }, [portadores, titular, rotuloPorChave]);

    const tableData = useMemo(() => {
        const linhas = portadores.map((p) => ({ key: p.chave, ...p }));
        if (titular) {
            linhas.push({
                key: 'titular',
                chave: 'titular',
                portador: 'Titular',
                cartao_nome: '—',
                total: titular.total,
                num_transacoes: titular.num_transacoes,
                ticket_medio: titular.ticket_medio,
                participacao_pct: titular.participacao_pct,
                por_categoria: titular.por_categoria || [],
                isTitular: true,
            });
        }
        return linhas;
    }, [portadores, titular]);

    const [composicaoModo, setComposicaoModo] = useState('valor');

    const linhasComCategorias = useMemo(() => {
        const linhas = portadores.map((p) => ({
            chave: p.chave,
            nome: rotuloPorChave[p.chave] || p.portador,
            total: p.total,
            cats: p.por_categoria || [],
        }));
        if (titular && titular.total > 0) {
            linhas.push({
                chave: 'titular',
                nome: 'Titular',
                total: titular.total,
                cats: titular.por_categoria || [],
                isTitular: true,
            });
        }
        return linhas;
    }, [portadores, titular, rotuloPorChave]);

    // "Qual é o maior gasto de cada pessoa" — categoria nº 1 de cada portador
    const maiorGastoPorPortador = useMemo(
        () =>
            linhasComCategorias
                .filter((l) => l.cats.length > 0)
                .map((l) => {
                    const top = l.cats[0];
                    return {
                        chave: l.chave,
                        nome: l.nome,
                        categoria: top.name,
                        valor: top.value,
                        pct: l.total > 0 ? (top.value / l.total) * 100 : 0,
                    };
                }),
        [linhasComCategorias],
    );

    // Composição de gastos por portador (barra empilhada horizontal)
    const composicao = useMemo(() => {
        const somaGlobal = {};
        linhasComCategorias.forEach((l) =>
            l.cats.forEach((c) => {
                somaGlobal[c.name] = (somaGlobal[c.name] || 0) + c.value;
            }),
        );
        const topCats = Object.entries(somaGlobal)
            .sort((a, b) => b[1] - a[1])
            .slice(0, MAX_CATEGORIAS)
            .map(([nome]) => nome);
        const topSet = new Set(topCats);

        let temOutras = false;
        const rows = linhasComCategorias.map((l) => {
            const row = { nome: l.nome, _total: l.total, _maior: l.cats[0]?.name || null };
            let outras = 0;
            l.cats.forEach((c) => {
                if (topSet.has(c.name)) row[c.name] = c.value;
                else outras += c.value;
            });
            if (outras > 0) {
                row.Outras = outras;
                temOutras = true;
            }
            if (composicaoModo === 'pct' && l.total > 0) {
                [...topCats, 'Outras'].forEach((k) => {
                    if (row[k] != null) row[k] = (row[k] / l.total) * 100;
                });
            }
            return row;
        });
        const keys = temOutras ? [...topCats, 'Outras'] : topCats;
        return { rows, keys };
    }, [linhasComCategorias, composicaoModo]);

    const handleExportExcel = () => {
        if (!data) return;
        const rows = tableData.map((p) => {
            const top = (p.por_categoria || [])[0];
            return {
                Portador: rotuloPorChave[p.chave] || p.portador,
                Cartão: p.cartao_nome,
                'Nº transações': p.num_transacoes,
                'Ticket médio': Number(p.ticket_medio || 0),
                'Participação %': Number(p.participacao_pct || 0),
                'Maior categoria': top ? top.name : '—',
                'Valor maior categoria': top ? Number(top.value) : 0,
                Total: Number(p.total || 0),
            };
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Por portador');

        // 2ª aba: detalhamento completo portador × categoria
        const detalhe = [];
        tableData.forEach((p) => {
            (p.por_categoria || []).forEach((c) => {
                detalhe.push({
                    Portador: rotuloPorChave[p.chave] || p.portador,
                    Categoria: c.name,
                    Valor: Number(c.value),
                });
            });
        });
        if (detalhe.length > 0) {
            XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.json_to_sheet(detalhe),
                'Portador x Categoria',
            );
        }
        XLSX.writeFile(wb, `saldo_ativo_portadores_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    };

    const columns = [
        {
            title: 'Portador',
            dataIndex: 'portador',
            key: 'portador',
            render: (text, record) => {
                const label = rotuloPorChave[record.chave] || text;
                return record.isTitular ? (
                    <Text type="secondary">{label}</Text>
                ) : (
                    <Text strong>{label}</Text>
                );
            },
        },
        {
            title: 'Cartão',
            dataIndex: 'cartao_nome',
            key: 'cartao_nome',
            responsive: ['md'],
            render: (text) => text || '—',
        },
        {
            title: 'Transações',
            dataIndex: 'num_transacoes',
            key: 'num_transacoes',
            align: 'right',
            responsive: ['sm'],
        },
        {
            title: 'Ticket médio',
            dataIndex: 'ticket_medio',
            key: 'ticket_medio',
            align: 'right',
            responsive: ['lg'],
            render: (v) => fmtBRL(v),
        },
        {
            title: 'Participação',
            dataIndex: 'participacao_pct',
            key: 'participacao_pct',
            align: 'right',
            render: (v) => `${(v || 0).toFixed(1)}%`,
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.total - b.total,
            render: (v) => <Text strong>{fmtBRL(v)}</Text>,
        },
    ];

    const temDados = data && (portadores.length > 0 || (titular && titular.total > 0));

    return (
        <>
            <Card style={{ marginBottom: 16 }}>
                <Form form={form} layout="vertical" initialValues={{ periodo: DEFAULT_RANGE }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                            <Form.Item name="periodo" label="Período">
                                <RangePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    presets={PRESETS}
                                    allowClear={false}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="cartoes" label="Cartões">
                                <Select
                                    mode="multiple"
                                    placeholder="Todos os cartões"
                                    allowClear
                                    maxTagCount="responsive"
                                    optionFilterProp="label"
                                    options={cartoes.map((c) => ({ value: c.id, label: c.nome }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Item
                                label={
                                    <Space size={4}>
                                        Regime
                                        <Tooltip title="Fatura: gasto entra pelo vencimento da fatura. Competência: pela data da compra.">
                                            <InfoCircleOutlined style={{ color: '#E6A23C' }} />
                                        </Tooltip>
                                    </Space>
                                }
                            >
                                <Segmented
                                    block
                                    value={regime}
                                    onChange={setRegime}
                                    options={[
                                        { label: 'Fatura', value: 'fatura' },
                                        { label: 'Compra', value: 'competencia' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Item
                                label={
                                    <Space size={4}>
                                        Agrupar
                                        <Tooltip title="Por nome funde adicionais de mesma pessoa em cartões diferentes.">
                                            <InfoCircleOutlined style={{ color: '#E6A23C' }} />
                                        </Tooltip>
                                    </Space>
                                }
                            >
                                <Segmented
                                    block
                                    value={groupBy}
                                    onChange={setGroupBy}
                                    options={[
                                        { label: 'Cartão', value: 'adicional' },
                                        { label: 'Nome', value: 'nome' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Space wrap>
                        <Button
                            type="primary"
                            icon={<FilterOutlined />}
                            loading={loading}
                            onClick={fetchDados}
                        >
                            Filtrar
                        </Button>
                        <Button
                            icon={<FileExcelOutlined />}
                            onClick={handleExportExcel}
                            disabled={!temDados}
                        >
                            Excel
                        </Button>
                    </Space>
                </Form>
            </Card>

            <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
                Considera apenas despesas de cartão de crédito já lançadas (não projeta recorrências
                futuras).
            </Text>

            {!temDados && !loading ? (
                <EmptyStateGuide
                    title="Nenhum gasto por portador no período."
                    description="Cadastre cartões adicionais e vincule as compras ao portador para ver a análise aqui."
                />
            ) : (
                <>
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        <Col xs={12} md={6}>
                            <Card size="small" loading={loading}>
                                <Statistic title="Gasto total" value={data?.total_geral || 0} precision={2} prefix="R$" />
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card size="small" loading={loading}>
                                <Statistic title="Portadores" value={portadores.length} />
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card size="small" loading={loading}>
                                <Statistic
                                    title="Maior gasto"
                                    value={portadores[0]?.total || 0}
                                    precision={2}
                                    prefix="R$"
                                    valueStyle={{ color: '#D64545' }}
                                />
                                {portadores[0] && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {rotuloPorChave[portadores[0].chave] || portadores[0].portador}
                                    </Text>
                                )}
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card size="small" loading={loading}>
                                <Statistic
                                    title="Gasto do titular"
                                    value={titular?.total || 0}
                                    precision={2}
                                    prefix="R$"
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Card title="Ranking de gastos" size="small" style={{ marginBottom: 16 }}>
                        <ResponsiveContainer
                            width="100%"
                            height={Math.max(200, rankingData.length * 44 + 24)}
                        >
                            <BarChart
                                data={rankingData}
                                layout="vertical"
                                margin={{ top: 8, right: 48, left: 8, bottom: 8 }}
                            >
                                <CartesianGrid stroke="#f0f0f0" horizontal={false} />
                                <XAxis
                                    type="number"
                                    tickFormatter={(v) => `R$ ${Math.round(v / 1000)}k`}
                                    tick={{ fontSize: 11 }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="nome"
                                    width={isMobile ? 120 : 170}
                                    tick={{ fontSize: 12 }}
                                />
                                <RTooltip formatter={(v) => fmtBRL(v)} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                <Bar dataKey="total" fill={chartPalette[0]} radius={[0, 4, 4, 0]} barSize={22} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>

                    {maiorGastoPorPortador.length > 0 && (
                        <Card
                            title="Maior gasto de cada portador"
                            size="small"
                            style={{ marginBottom: 16 }}
                        >
                            <Row gutter={[12, 12]}>
                                {maiorGastoPorPortador.map((m) => (
                                    <Col xs={12} sm={8} lg={6} key={m.chave}>
                                        <div
                                            style={{
                                                border: '1px solid #f0f0f0',
                                                borderRadius: 8,
                                                padding: '10px 12px',
                                                height: '100%',
                                            }}
                                        >
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: 12, display: 'block' }}
                                                ellipsis={{ tooltip: m.nome }}
                                            >
                                                {m.nome}
                                            </Text>
                                            <div
                                                style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}
                                                title={m.categoria}
                                            >
                                                {m.categoria}
                                            </div>
                                            <Text style={{ fontSize: 13 }}>
                                                {fmtBRL(m.valor)}
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {' '}· {m.pct.toFixed(0)}% do gasto dele
                                                </Text>
                                            </Text>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    )}

                    <Card
                        size="small"
                        style={{ marginBottom: 16 }}
                        title={
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 8,
                                }}
                            >
                                <span>Composição de gastos por portador</span>
                                <Segmented
                                    size="small"
                                    value={composicaoModo}
                                    onChange={setComposicaoModo}
                                    options={[
                                        { label: 'R$', value: 'valor' },
                                        { label: '%', value: 'pct' },
                                    ]}
                                />
                            </div>
                        }
                    >
                        {composicao.rows.length === 0 || composicao.keys.length === 0 ? (
                            <Text type="secondary">Sem detalhamento por categoria no período.</Text>
                        ) : (
                            <ResponsiveContainer
                                width="100%"
                                height={Math.max(220, composicao.rows.length * 54 + 60)}
                            >
                                <BarChart
                                    data={composicao.rows}
                                    layout="vertical"
                                    margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                                >
                                    <CartesianGrid stroke="#f0f0f0" horizontal={false} />
                                    <XAxis
                                        type="number"
                                        domain={composicaoModo === 'pct' ? [0, 100] : undefined}
                                        tickFormatter={(v) =>
                                            composicaoModo === 'pct'
                                                ? `${v}%`
                                                : `R$ ${Math.round(v / 1000)}k`
                                        }
                                        tick={{ fontSize: 11 }}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="nome"
                                        width={isMobile ? 120 : 170}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <RTooltip
                                        content={<ComposicaoTooltip modo={composicaoModo} />}
                                        cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    {composicao.keys.map((key, idx) => (
                                        <Bar
                                            key={key}
                                            dataKey={key}
                                            stackId="a"
                                            fill={catColor(key, idx)}
                                            radius={
                                                idx === composicao.keys.length - 1
                                                    ? [0, 4, 4, 0]
                                                    : [0, 0, 0, 0]
                                            }
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Card>

                    <Card size="small">
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            loading={loading}
                            size={isMobile ? 'small' : 'middle'}
                            pagination={false}
                            scroll={{ x: 640 }}
                            expandable={{
                                expandedRowRender: (record) => (
                                    <Space wrap size={[8, 8]}>
                                        {(record.por_categoria || []).length === 0 ? (
                                            <Text type="secondary">Sem detalhamento por categoria.</Text>
                                        ) : (
                                            record.por_categoria.map((c) => (
                                                <Tag key={c.name} color="default">
                                                    {c.name}: {fmtBRL(c.value)}
                                                </Tag>
                                            ))
                                        )}
                                    </Space>
                                ),
                            }}
                        />
                    </Card>
                </>
            )}
        </>
    );
};

export default RelatoriosPortadorTab;
